import { useRef, useEffect, useMemo } from 'react'
import { Eye, MousePointer2 } from 'lucide-react'

/**
 * Renders HTML email body inside a sandboxed iframe.
 */
function EmailBody({ body, isSent }) {
  const iframeRef = useRef(null)
  const isHTML = useMemo(() => body && /<[a-z][\s\S]*>/i.test(body), [body])

  useEffect(() => {
    if (!isHTML || !iframeRef.current) return
    const iframe = iframeRef.current
    const onLoad = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document
        const h = doc.documentElement.scrollHeight || doc.body.scrollHeight
        iframe.style.height = h + 'px'
      } catch { /* cross-origin fallback */ }
    }
    iframe.addEventListener('load', onLoad)
    return () => iframe.removeEventListener('load', onLoad)
  }, [isHTML, body])

  if (!isHTML) {
    return (
      <div className="rh-msg-bubble" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {body}
      </div>
    )
  }

  return (
    <div className={`rh-msg-bubble rh-msg-bubble--html ${isSent ? 'rh-msg-bubble--sent' : ''}`}>
      <iframe
        ref={iframeRef}
        srcDoc={body}
        sandbox="allow-same-origin"
        title="Email content"
        className="rh-email-iframe"
      />
    </div>
  )
}

export default function MessageBubble({ message, convFromName }) {
  const isSent = message.type === 'sent' || message.direction === 'sent'
  const time = message.time || message.sentAt

  // Tracking applies only to outbound campaign sends with a sendLogId
  const hasSendLog = isSent && !!message.sendLogId
  const opens = Number(message.openCount || 0)
  const clicks = Number(message.clickCount || 0)
  const showOpens = hasSendLog && opens > 0
  const showClicks = hasSendLog && clicks > 0

  const trackingAriaLabel = (() => {
    if (!isSent) return undefined
    const parts = []
    if (showOpens) parts.push(`Opened ${opens} ${opens === 1 ? 'time' : 'times'}`)
    if (showClicks) parts.push(`Clicked ${clicks} ${clicks === 1 ? 'link' : 'links'}`)
    return parts.length ? parts.join('. ') : undefined
  })()

  return (
    <div className={`rh-email-msg ${isSent ? 'sent' : 'received'}`}>
      <div className="rh-msg-avatar">
        {isSent ? 'Y' : (convFromName || '?').charAt(0)}
      </div>
      <div className="rh-msg-bubble-wrap">
        <div className="rh-msg-meta">
          <span className="rh-msg-from">{message.from}</span>
          {!isSent && <span>{time}</span>}
        </div>
        <EmailBody body={message.body} isSent={isSent} />
        {isSent && (
          <div className="rh-msg-tracking" aria-label={trackingAriaLabel}>
            <span className="rh-msg-tracking__time">{time}</span>
            {showOpens && (
              <>
                <span className="rh-msg-tracking__sep" aria-hidden="true">·</span>
                <Eye size={11} aria-hidden="true" />
                <span>{opens}</span>
              </>
            )}
            {showClicks && (
              <>
                <span className="rh-msg-tracking__sep" aria-hidden="true">·</span>
                <MousePointer2 size={11} aria-hidden="true" />
                <span>{clicks}</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
